import { SUPERNOVA_TOKENS } from "./supernovaContracts";

/**
 * supernovaSummary — pure derivation of everything the Supernova read view
 * shows, from the service slices. No React, no formatting, no I/O.
 *
 * WHY THIS FILE EXISTS: every figure on the tab has to carry its own status.
 * The tab renders four independent metrics that fail independently, and the one
 * thing it must never do is collapse "we couldn't read it" or "that backend
 * isn't built" into a zero. So each metric comes out of here as
 * `{ status, value, meta }` and the components render the status, not a number
 * they had to guess at.
 *
 * METRIC STATUS VOCABULARY (mirrors useSupernovaChain):
 *   "idle"         nothing requested — no wallet connected
 *   "loading"      read in flight → skeleton
 *   "ready"        `value` is real, from the chain
 *   "unavailable"  that backend isn't wired up yet → labelled placeholder
 *   "error"        the read failed → error state with `message`
 *
 * CUSTODY NOTE (drives the Owned/Staked arithmetic below): staking transfers
 * the NFT into the staking contract, so a staked NFT is NOT in the member's
 * ERC-1155 balance. `getOwned().totalOwned` is therefore "in your wallet, free
 * to stake". The headline "Owned" figure is wallet + staked, and it is only
 * computed when BOTH reads succeeded — a wallet count added to an unknown
 * staked count would be a smaller number presented as a total.
 *
 * COMPLIANCE: nothing here is a return, a yield, a price, or a projection. XP
 * is an in-app points balance passed straight through from its source.
 */

export const METRIC = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  UNAVAILABLE: "unavailable",
  ERROR: "error",
};

/** Placeholder copy for the two backends that aren't connected yet. */
export const STAKE_UNAVAILABLE_NOTE =
  "Stake data isn't connected yet. Your on-chain stakes are unaffected.";
export const SLOTS_UNAVAILABLE_NOTE =
  "Slot usage comes from Strategy Automation and isn't connected here yet.";
export const XP_UNAVAILABLE_NOTE =
  "XP balances come from the staking engine, which isn't connected yet.";

const metric = (status, value = null, extra = {}) => ({
  status,
  value,
  meta: null,
  note: null,
  message: null,
  ...extra,
});

/** Member-safe message off a SupernovaChainError, with a plain fallback. */
const errorMessage = (error, fallback) =>
  (error && typeof error.message === "string" && error.message) || fallback;

/**
 * Map a slice status onto a metric, so a metric derived from a slice inherits
 * loading / error / unavailable without each caller re-deriving it.
 */
function fromSlice(status, error, fallbackMessage, unavailableNote) {
  switch (status) {
    case "loading":
      return metric(METRIC.LOADING);
    case "unavailable":
      return metric(METRIC.UNAVAILABLE, null, {
        note: errorMessage(error, unavailableNote),
      });
    case "error":
      return metric(METRIC.ERROR, null, {
        message: errorMessage(error, fallbackMessage),
      });
    default:
      return metric(METRIC.IDLE);
  }
}

/** staked NFTs per token id, from the open lots. */
function stakedByToken(staked) {
  const counts = new Map();
  (staked?.lots || []).forEach((lot) => {
    counts.set(lot.tokenId, (counts.get(lot.tokenId) || 0) + lot.qty);
  });
  return counts;
}

/**
 * @param {Object} input
 * @param {Object|null} input.owned          getOwned() result
 * @param {string} input.ownedStatus
 * @param {Error|null} [input.ownedError]
 * @param {Object|null} input.staked         getStaked() result
 * @param {string} input.stakedStatus
 * @param {Error|null} [input.stakedError]
 * @param {number|null} [input.slotsUsed]    strategies currently automated
 * @param {string} [input.slotsUsedStatus]
 * @param {number|null} [input.xpBalance]    XP points from the staking engine
 * @param {string} [input.xpBalanceStatus]
 */
export function summarizeSupernova(input = {}) {
  const {
    owned = null,
    ownedStatus = METRIC.IDLE,
    ownedError = null,
    staked = null,
    stakedStatus = METRIC.IDLE,
    stakedError = null,

    /* TODO(backend, supervised): slot usage lives in the Strategy Automation
       engine (allocations on GET /status). This tab does not read that engine —
       cross-linking the two surfaces is SUP-6. Until it is passed in, slot
       usage is UNAVAILABLE, never 0. */
    slotsUsed = null,
    slotsUsedStatus = METRIC.UNAVAILABLE,

    /* TODO(backend, supervised): the XP balance is the staking engine's number
       (SUPERNOVA_ENGINE_DESIGN.md §6.2), not something the tab may compute:
       accrual on open lots is only part of it, and XP banked from closed lots
       is invisible from the chain reads this tab has. Deriving a partial figure
       and labelling it "XP balance" would be a wrong number that looks right,
       so it stays UNAVAILABLE until the engine serves it. */
    xpBalance = null,
    xpBalanceStatus = METRIC.UNAVAILABLE,
  } = input;

  const ownedReady = ownedStatus === METRIC.READY && !!owned;
  const stakedReady = stakedStatus === METRIC.READY && !!staked;

  const walletCount = ownedReady ? owned.totalOwned : null;
  const stakedCount = stakedReady ? staked.stakedCount : null;

  /* ── Owned ─────────────────────────────────────────────────────────────
     Wallet balance plus what the staking contract holds. Only a total once
     both halves are real; with staked unknown we show the wallet count and say
     so, rather than passing a partial off as the total. */
  let ownedMetric;
  if (ownedReady) {
    const typesHeld = owned.tokens.filter((t) => t.owned > 0).length;
    const typeMeta = `${typesHeld} of ${owned.tokens.length} types held`;
    ownedMetric = stakedReady
      ? metric(METRIC.READY, walletCount + stakedCount, { meta: typeMeta })
      : metric(METRIC.READY, walletCount, {
          meta: `in your wallet · ${typeMeta}`,
          note: STAKE_UNAVAILABLE_NOTE,
        });
  } else {
    ownedMetric = fromSlice(
      ownedStatus,
      ownedError,
      "Couldn't read your Supernova collection.",
      "Your collection isn't connected yet."
    );
  }

  /* ── Staked ────────────────────────────────────────────────────────────
     The wallet balance IS the free-to-stake count, because staking moves the
     NFT out of the wallet. */
  const stakedMetric = stakedReady
    ? metric(METRIC.READY, stakedCount, {
        meta: ownedReady ? `${walletCount.toLocaleString("en-US")} available to stake` : null,
      })
    : fromSlice(
        stakedStatus,
        stakedError,
        "Couldn't read your stakes.",
        STAKE_UNAVAILABLE_NOTE
      );

  /* ── Strategy slots ────────────────────────────────────────────────────
     One staked Supernova = one slot, so the TOTAL is the staked count. USAGE
     is the automation engine's number and may be unknown on its own. */
  const slotsUsedKnown =
    slotsUsedStatus === METRIC.READY && typeof slotsUsed === "number";
  let slotsMetric;
  if (stakedReady) {
    const free = slotsUsedKnown ? Math.max(0, stakedCount - slotsUsed) : null;
    slotsMetric = metric(METRIC.READY, null, {
      used: slotsUsedKnown ? slotsUsed : null,
      total: stakedCount,
      meta: !slotsUsedKnown
        ? "slots unlocked · usage not connected"
        : slotsUsed > stakedCount
        ? "over-allocated"
        : free > 0
        ? `${free.toLocaleString("en-US")} free`
        : "all in use",
      note: slotsUsedKnown ? null : SLOTS_UNAVAILABLE_NOTE,
    });
  } else {
    slotsMetric = fromSlice(
      stakedStatus,
      stakedError,
      "Couldn't read your stakes, so slots are unknown.",
      STAKE_UNAVAILABLE_NOTE
    );
    slotsMetric.used = null;
    slotsMetric.total = null;
  }

  /* ── XP balance ────────────────────────────────────────────────────────── */
  const xpMetric =
    xpBalanceStatus === METRIC.READY && typeof xpBalance === "number"
      ? metric(METRIC.READY, xpBalance, { meta: "in-app points · no monetary value" })
      : fromSlice(xpBalanceStatus, null, "Couldn't read your XP balance.", XP_UNAVAILABLE_NOTE);

  /* ── Per-token rows ────────────────────────────────────────────────────
     Always the full collection, so a member with none of a type still sees it
     exists. Counts stay null (not 0) whenever their read isn't ready. */
  const perToken = stakedByToken(staked);
  const ownedByToken = new Map((owned?.tokens || []).map((t) => [t.id, t.owned]));
  const tokens = SUPERNOVA_TOKENS.map(({ id, name }) => {
    const inWallet = ownedReady ? ownedByToken.get(id) ?? 0 : null;
    const inStake = stakedReady ? perToken.get(id) || 0 : null;
    return {
      id,
      name,
      owned: ownedReady && stakedReady ? inWallet + inStake : inWallet,
      inWallet,
      staked: inStake,
      ownedStatus: ownedMetric.status,
      stakedStatus: stakedMetric.status,
      /* Nothing is stakeable that isn't sitting in the member's own wallet. */
      stakeable: ownedReady ? inWallet > 0 : false,
    };
  });

  /* ── Header badge ──────────────────────────────────────────────────────── */
  const headline = stakedReady
    ? { status: METRIC.READY, stakedCount, slotCount: stakedCount }
    : {
        status: stakedMetric.status,
        stakedCount: null,
        slotCount: null,
        note: stakedMetric.note || stakedMetric.message || STAKE_UNAVAILABLE_NOTE,
      };

  return {
    owned: ownedMetric,
    staked: stakedMetric,
    slots: slotsMetric,
    xp: xpMetric,
    tokens,
    headline,
    /* True only when the chain actually answered and the answer was nothing —
       an empty wallet, not an unread one. */
    isEmptyCollection:
      ownedReady &&
      walletCount === 0 &&
      (!stakedReady || stakedCount === 0),
    /* Whether we can back up a statement about stakes. The empty state reads
       differently depending on this: with the stake read unavailable we may say
       the wallet is empty, but NOT that nothing is staked. */
    stakeStateKnown: stakedReady,
  };
}

export default summarizeSupernova;
